import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api, { formatApiError } from '../api';
import { Eye, EyeOff } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

function getPasswordStrength(pw) {
  if (!pw) return null;
  const hasUpper = /[A-Z]/.test(pw);
  const hasNumOrSym = /[0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pw);
  if (pw.length < 8 || pw === pw.toLowerCase()) return { level: 'weak', label: 'Svakt', color: 'bg-red-500', width: 'w-1/3' };
  if (pw.length >= 8 && !(hasUpper && hasNumOrSym)) return { level: 'fair', label: 'Middels', color: 'bg-yellow-500', width: 'w-2/3' };
  return { level: 'strong', label: 'Sterkt', color: 'bg-green-500', width: 'w-full' };
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      setError('Tilbakestillingslenken er ugyldig eller utløpt');
      return;
    }
    if (password.length < 8) {
      setError('Passordet må være minst 8 tegn');
      return;
    }
    if (password !== confirm) {
      setError('Passordene er ikke like');
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/auth/reset-password', { token, password });
      setSuccess('Passordet er oppdatert. Sender deg til innlogging...');
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = formatApiError(detail) || 'Tilbakestillingslenken er ugyldig eller utløpt';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative bg-navy-900" data-testid="reset-password-page">
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher />
      </div>

      <div className="w-full flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <img src="/logo.png" alt="logo" className="w-9 h-9 rounded-lg object-cover" />
            <span className="font-heading text-xl font-bold text-white tracking-tight">AI Resepsjonen</span>
          </div>

          <h2 className="font-heading text-2xl font-bold text-white tracking-tight mb-2">Sett nytt passord</h2>
          <p className="text-slate-500 mb-8">Velg et nytt passord for kontoen din.</p>

          {error && (
            <div className="bg-accent-red/10 border border-accent-red/20 text-accent-red px-4 py-3 rounded-lg mb-6 text-sm" data-testid="reset-password-error">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-accent-green/10 border border-accent-green/20 text-accent-green px-4 py-3 rounded-lg mb-6 text-sm" data-testid="reset-password-success">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Nytt passord</label>
              <div className="relative">
                <input
                  data-testid="reset-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-colors pr-12"
                  placeholder="Minst 8 tegn"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {password && (() => {
                const strength = getPasswordStrength(password);
                return (
                  <div className="mt-2">
                    <div className="h-1.5 bg-navy-700 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-300 ${strength.color} ${strength.width}`} />
                    </div>
                    <p className={`text-xs mt-1 ${
                      strength.level === 'weak' ? 'text-red-400' :
                      strength.level === 'fair' ? 'text-yellow-400' : 'text-green-400'
                    }`}>{strength.label} passord</p>
                  </div>
                );
              })()}
            </div>

            <div>
              <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Bekreft passord</label>
              <input
                data-testid="reset-password-confirm"
                type={showPassword ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-4 py-3 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-colors"
                placeholder="Skriv passordet på nytt"
                required
                minLength={8}
              />
            </div>

            <button
              data-testid="reset-password-submit"
              type="submit"
              disabled={loading || !token}
              className="w-full py-3 bg-accent-blue hover:bg-accent-blue-hover text-white font-semibold rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Oppdaterer...' : 'Oppdater passord'}
            </button>

            <div className="text-center">
              <Link to="/login" className="text-xs text-accent-blue hover:text-accent-blue-hover transition-colors">
                Tilbake til innlogging
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
